/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { fieldsWithoutDefaults } from "cms/fields/field-utils";
import { isUneditableField } from "cms/fields/dynamic-field/is-uneditable-field";
import { Logger } from "cms/utils/log/logger";

/**
 * initializeField
 * 
 * @param {object} schemaField
 * @param {object} field
 * @param {object} defaultImpl
 * @returns {SimpliCityField}
 */
export function initializeField (schemaField, field, defaultImpl) {

    const type = schemaField?.type;
    const name = schemaField?.name;
    const position = schemaField?.position;
    const list = schemaField?.list;
    const variant = schemaField?.variant;
    const schemaSettings = schemaField?.settings;

    if (isUneditableField(name)) {
        return;
    }
    
    // const noOverride = schemaSettings?.defaultFormat ?? false;
    const hidden = schemaSettings?.hidden ?? false;
    const defaultStyle = schemaSettings?.defaultStyle;
    const defaultValue = schemaSettings?.defaultValue;
    const defaultFormat = schemaSettings?.defaultFormat;

    const values = field?.values ?? [];
    let settings = field?.settings;

    // const { hide, style, format, options } = settings || {}
    settings = cloneSettings(settings);
    if (hidden) {
        settings.hide = true;
    }
    
    const isWithoutDefaults = fieldsWithoutDefaults.includes(type);
    // apply one default item for entire list
    const hasDefaultValue = defaultValue != null;
    const hasDefaultStyle = defaultStyle != null;
    const canUseDefaults = !isWithoutDefaults && hasDefaultValue && hasDefaultStyle;
    // for entire list
    let wasAlreadyDefault = true;

    for (const item of values) {
        const value = item?.value;
        const settings = item?.settings;
        const style = settings?.style;
        const format = settings?.format;
        const isDefaultValue = defaultValue === value;
        const isDefaultStyle = defaultStyle === style;
        const isDefaultFormat = defaultFormat === format;
        const flags = [isDefaultValue, isDefaultStyle, isDefaultFormat];
        const areDefaults = validateAllTrue(flags);
        wasAlreadyDefault = wasAlreadyDefault && areDefaults;
        Logger.info(`Initializing Field ${name} ${JSON.stringify(value)}`);
    }

    // if no values initialize one
    if (values.length === 0) {
        values.push(defaultImpl);
    }

    return {
        original: field,
        name,
        type,
        list,
        variant,
        position,
        values,
        settings,
        canUseDefaults,
        wasAlreadyDefault,
        defaultImpl,
    };
}

/**
 * cloneSettings
 * used to ensure that settings are not mutating previous values
 * @param settings
 * @returns {*}
 */
export function cloneSettings (settings) {
    return { ...settings };
}

/**
 * validateAllTrue
 * Checks that all flags are true; one or more false flag should cause it to fail
 * @param flags
 * @returns {*}
 */
export function validateAllTrue (flags) {
    if (!Array.isArray(flags)) {
        throw new Error("field flags was not an array");
    }
    return flags.every((flag) => {
        return flag === true;
    });
}
